import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class OrganizationSettingsService {
  private readonly threatPairModuleEnabledSubject = new BehaviorSubject<
    boolean | null
  >(null);

  readonly threatPairModuleEnabled$: Observable<boolean | null> =
    this.threatPairModuleEnabledSubject.asObservable();

  get threatPairModuleEnabled(): boolean | null {
    return this.threatPairModuleEnabledSubject.value;
  }

  isThreatPairModuleOn(): boolean {
    return this.threatPairModuleEnabledSubject.value === true;
  }

  private static normalizeThreatPairEnabledFlag(raw: unknown): boolean {
    return raw === true;
  }

  applyFromOrganizationPayload(org: any): void {
    const settings = org?.settings;
    if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
      this.threatPairModuleEnabledSubject.next(false);
      return;
    }
    this.threatPairModuleEnabledSubject.next(
      OrganizationSettingsService.normalizeThreatPairEnabledFlag(
        settings.threatPairModuleEnabled,
      ),
    );
  }

  /**
   * Login / session hydrate from plainUser (or JWT org).
   * Returns true only when a threat-pair flag was actually present — missing
   * settings leave the subject unchanged (still null) so callers can fall back.
   */
  applyFromPlainUser(plainUser: any): boolean {
    const org = plainUser?.organization;
    const settings = org?.settings;
    if (settings && typeof settings === 'object' && !Array.isArray(settings)) {
      this.threatPairModuleEnabledSubject.next(
        OrganizationSettingsService.normalizeThreatPairEnabledFlag(
          settings.threatPairModuleEnabled,
        ),
      );
      return true;
    }
    // Alternate shapes some login payloads may use
    const raw =
      plainUser?.threatPairModuleEnabled ??
      org?.threatPairModuleEnabled ??
      plainUser?.organizationInfo?.settings?.threatPairModuleEnabled;
    if (raw === true || raw === false) {
      this.threatPairModuleEnabledSubject.next(raw === true);
      return true;
    }
    return false;
  }

  applyFromAdminSelfPayload(data: any): void {
    this.applyFromOrganizationPayload(data?.organizationInfo);
  }

  syncThreatPairIfCurrentOrg(
    editedOrganizationId: string,
    threatPairModuleEnabled: boolean,
    currentUserOrganizationId: string | null | undefined,
  ): void {
    if (
      currentUserOrganizationId != null &&
      String(editedOrganizationId) === String(currentUserOrganizationId)
    ) {
      this.threatPairModuleEnabledSubject.next(threatPairModuleEnabled);
    }
  }

  /** Reset before logout / full navigation away. */
  reset(): void {
    this.threatPairModuleEnabledSubject.next(null);
  }
}
